import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import config from '../../config/config';
import DataTable from 'react-data-table-component';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DashboardLayout from '../shared/layout/DashboardLayout';
import EmailReminderBanner from '../../components/common/EmailReminderBanner';
import '../../styles/App.css';
import DataChart from '../../components/common/DataChart';
import { Tooltip } from 'react-tooltip';
import Modal from '../../components/common/Modal';
import { FaEye, FaArchive, FaFolderOpen } from 'react-icons/fa';
import { getAdminMenuItems, getSupervisorMenuItems } from '../../utils/menuItems';

const AdminPage = () => {
  const tableRef = useRef(null);
  const [dataList, setDataList] = useState([]);
  const [expandedIGT, setExpandedIGT] = useState(null);
  const [igtTableData, setIgtTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [userEmail, setUserEmail] = useState(null); // null = loading, '' = empty, 'xxx' = has email
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveTicket, setArchiveTicket] = useState({ noTiket: null, currentStatus: false });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [stats, setStats] = useState({
    uploadedKL: 0,
    integratedData: 0,
    nonIntegratedData: 0,
    totalData: 0,
    totalUniqueIGT: 0,
    archivedData: 0
  });
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterTitle, setFilterTitle] = useState('Semua Data Aktif');
  const [klList, setKlList] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [igtData, setIgtData] = useState([]);
  const [showArchivedData, setShowArchivedData] = useState(false);

  // Fetch user profile untuk cek email
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setUserEmail(''); // No token, set as empty (not logged in)
          return;
        }
        
        const response = await axios.get(`${config.API_URL}/profile`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        setUserEmail(response.data.email || '');
      } catch (err) {
        console.error('Gagal mengambil data profil:', err);
        setUserEmail('');
      }
    };
    fetchProfile();
  }, []);

  // Fungsi untuk ekstrak kode IGT dari nomor tiket
  const extractIGTCode = (noTiket) => {
    if (!noTiket || typeof noTiket !== 'string') return null;
    if (noTiket.includes('-')) {
      const parts = noTiket.split('-');
      return parts[0].substring(0, 6);
    }
    return noTiket.substring(0, 6);
  };

  // Fungsi untuk mengurutkan data dengan nomor tiket 
  const sortDataByTicket = (data, ascending = false) => {
    return [...data].sort((a, b) => {
      const ticketCompare = ascending
        ? a.no_tiket.localeCompare(b.no_tiket)
        : b.no_tiket.localeCompare(a.no_tiket);

      if (ticketCompare !== 0) return ticketCompare;
      const dateA = new Date(a.last_modified || 0);
      const dateB = new Date(b.last_modified || 0);
      return dateB - dateA;
    });
  };

  // Fungsi untuk mengurutkan data
  const sortDataWithSecondary = (data, secondaryField, secondaryAsc = true) => {
    if (expandedIGT) {
      return data;
    }

    return [...data].sort((a, b) => {
      if (a.is_igt_summary || b.is_igt_summary || a.is_detail_row || b.is_detail_row) {
        return 0; 
      }

      if (secondaryField && a[secondaryField] !== undefined && b[secondaryField] !== undefined) {
        const valA = a[secondaryField];
        const valB = b[secondaryField];

        if (secondaryField === 'last_modified') {
          const dateA = new Date(valA || 0);
          const dateB = new Date(valB || 0);
          return secondaryAsc ? dateA - dateB : dateB - dateA;
        } else {
          const compare = valA.toString().localeCompare(valB.toString());
          return secondaryAsc ? compare : -compare;
        }
      }

      return b.no_tiket.localeCompare(a.no_tiket);
    });
  };

  // Fungsi untuk menghitung total IGT unik dan status integrasinya
  const calculateUniqueIGTs = (data) => {
    const igtMap = new Map();

    data.forEach(item => {
      const groupName = item.nama_igt;

      if (groupName) {
        if (!igtMap.has(groupName)) {
          igtMap.set(groupName, {
            groupName: groupName,
            displayCode: extractIGTCode(item.no_tiket),
            count: 1,
            integratedCount: item.status === 'Sudah Terintegrasi' ? 1 : 0,
            nonIntegratedCount: item.status !== 'Sudah Terintegrasi' ? 1 : 0,
            records: [item],
            hasIntegratedData: item.status === 'Sudah Terintegrasi'
          });
        } else {
          const igtInfo = igtMap.get(groupName);
          igtInfo.count += 1;
          if (item.status === 'Sudah Terintegrasi') {
            igtInfo.integratedCount += 1;
            igtInfo.hasIntegratedData = true;
          } else {
            igtInfo.nonIntegratedCount += 1;
          }
          igtInfo.records.push(item);
        }
      }
    });
    const igtArray = Array.from(igtMap.values()).sort((a, b) => {
      return b.displayCode.localeCompare(a.displayCode);
    });
    setIgtData(igtArray);

    const integratedIGTCount = igtArray.filter(igt => igt.hasIntegratedData).length;
    const nonIntegratedIGTCount = igtArray.filter(igt => !igt.hasIntegratedData).length;

    return {
      totalUniqueIGT: igtMap.size,
      integratedIGTCount,
      nonIntegratedIGTCount
    };
  };

  const fetchData = async (showArchived = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const decoded = jwtDecode(token);
      if (!token || !decoded.twoFactorVerified) {
        navigate('/');
        return;
      }

      if (decoded.role !== 0 && decoded.role !== 2) {
        navigate('/');
        return;
      }
      
      try {
        const userResponse = await axios.get(`${config.API_URL}/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUsername(userResponse.data.fullname || decoded.username);
      } catch (error) {
        console.error('Failed to fetch user fullname:', error);
        setUsername(decoded.username);
      }
      
      setUserRole(decoded.role);

      const archiveParam = showArchived ? 'true' : 'false';
      const response = await axios.get(`${config.API_URL}/dataAdmin?show_archived=${archiveParam}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const sortedData = sortDataByTicket(response.data, false);

      setDataList(sortedData);
      setFilteredData(sortedData);
      setChartData(sortedData);

      const uniqueKLs = [...new Set(sortedData.map(item => item.kl))];
      const igtStats = calculateUniqueIGTs(sortedData);

      setKlList(uniqueKLs);

      setStats({
        uploadedKL: uniqueKLs.length,
        integratedData: igtStats.integratedIGTCount,
        nonIntegratedData: igtStats.nonIntegratedIGTCount,
        totalData: sortedData.length,
        totalUniqueIGT: igtStats.totalUniqueIGT,
        archivedData: 0
      });
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Tidak Ada Data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(showArchivedData);
  }, [navigate, showArchivedData]);

  const handleSearch = () => {
    setSearchLoading(true);
    setTimeout(() => {
      let filtered = [...dataList];

      if (searchText) {
        const query = searchText.toLowerCase();
        filtered = filtered.filter((row) =>
          row.no_tiket.toLowerCase().includes(query) ||
          row.nama_igt.toLowerCase().includes(query) ||
          row.kl.toLowerCase().includes(query) ||
          row.status.toLowerCase().includes(query)
        );
      }

      if (activeFilter === 'integrated') {
        filtered = filtered.filter(row => row.status === 'Sudah Terintegrasi');
      } else if (activeFilter === 'non-integrated') {
        filtered = filtered.filter(row => row.status !== 'Sudah Terintegrasi');
      }
      setFilteredData(sortDataByTicket(filtered, false));
      setSearchLoading(false);
    }, 500);
  };

  const handleStatCardClick = (type) => {
    setExpandedIGT(null);

    if (type === 'all') {
      setActiveFilter(null);
      setFilterTitle(showArchivedData ? 'Semua Data Arsip' : 'Semua Data Aktif');
      setFilteredData(sortDataByTicket(dataList, false));
    } else if (type === 'archived') {
      setShowArchivedData(!showArchivedData);
      setActiveFilter(null);
      setFilterTitle(!showArchivedData ? 'Data Arsip' : 'Semua Data Aktif');
    } else if (type === 'integrated') {
      setActiveFilter('integrated');
      setFilterTitle('Data Sudah Terintegrasi');
      const filtered = dataList.filter(item => item.status === 'Sudah Terintegrasi');
      setFilteredData(sortDataByTicket(filtered, false));
    } else if (type === 'non-integrated') {
      setActiveFilter('non-integrated');
      setFilterTitle('Data Belum Terintegrasi');
      const filtered = dataList.filter(item => item.status !== 'Sudah Terintegrasi');
      setFilteredData(sortDataByTicket(filtered, false));
    } else if (type === 'kl-list') {
      setActiveFilter('kl-list');
      setFilterTitle('Daftar K/L yang Sudah Upload');

      const klMap = new Map();
      dataList.forEach(item => {
        if (!klMap.has(item.kl) || new Date(item.last_modified) > new Date(klMap.get(item.kl).last_modified)) {
          klMap.set(item.kl, item);
        }
      });

      const klSummary = Array.from(klMap.values()).sort((a, b) => a.kl.localeCompare(b.kl));
      setFilteredData(klSummary);
    } else if (type === 'igt-list') {
      setActiveFilter('igt-list');
      setFilterTitle('Daftar Data IGT');

      const tableData = igtData
        .map(igt => ({
          no_tiket: igt.displayCode,
          nama_igt: igt.groupName,
          kl: `${igt.count} Data`,
          status: `${igt.integratedCount} Terintegrasi / ${igt.nonIntegratedCount} Belum`,
          verifikator: '-',
          last_modified: null,
          is_igt_summary: true,
          igt_code: igt.displayCode,
          isExpanded: false
        }))
        .sort((a, b) => b.no_tiket.localeCompare(a.no_tiket)); // Z ke A

      setIgtTableData(tableData);
      setFilteredData(tableData);
    }

    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const handleIGTClick = (clickedCode) => {
    if (activeFilter === 'igt-list' && expandedIGT === clickedCode) {
      setExpandedIGT(null);
      handleStatCardClick('igt-list');
      return;
    }

    setExpandedIGT(clickedCode);
    setFilterTitle(`Detail Data IGT: ${clickedCode}`);

    const targetGroup = igtData.find(group => group.displayCode === clickedCode);
    if (!targetGroup) return;

    const groupIdentifier = targetGroup.groupName;

    const detailData = dataList
      .filter(item => item.nama_igt === groupIdentifier)
      .sort((a, b) => b.no_tiket.localeCompare(a.no_tiket))
      .map(item => ({
        ...item,
        is_detail_row: true,
        igt_parent: clickedCode
      }));
    const updatedSummaryData = igtData
      .map(igt => ({
        no_tiket: igt.displayCode,
        nama_igt: igt.groupName,
        kl: `${igt.count} Data`,
        status: `${igt.integratedCount} Terintegrasi / ${igt.nonIntegratedCount} Belum`,
        verifikator: '-',
        last_modified: null,
        is_igt_summary: true,
        igt_code: igt.displayCode,
        isExpanded: igt.displayCode === clickedCode
      }))
      .sort((a, b) => b.no_tiket.localeCompare(a.no_tiket));

    // Gabungkan data dengan urutan yang benar
    const combinedData = [];
    updatedSummaryData.forEach(summaryRow => {
      combinedData.push(summaryRow);
      if (summaryRow.igt_code === clickedCode) {
        combinedData.push(...detailData);
      }
    });

    setIgtTableData(updatedSummaryData);
    setFilteredData(combinedData);

    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const handleChartClick = (data) => {
    console.log("Chart clicked with data:", data);
    if (data && data.name) {
      setActiveFilter('custom');
      setExpandedIGT(null);
      setFilterTitle(`Data ${data.name}`);
      const filtered = dataList.filter(item => item.status === data.name);
      setFilteredData(sortDataByTicket(filtered, false));
    }
    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const handleKLClick = (kl) => {
    setActiveFilter('custom-kl');
    setExpandedIGT(null);
    setFilterTitle(`Data dari ${kl}`);
    const filtered = dataList.filter(item => item.kl === kl);
    setFilteredData(sortDataByTicket(filtered, false));
  };

  const handleToggleArchive = (noTiket, isArchived) => {
    setArchiveTicket({ noTiket, currentStatus: isArchived });
    setShowArchiveModal(true);
  };

  const handleConfirmArchive = async () => {
    const { noTiket, currentStatus } = archiveTicket;
    if (!noTiket) return;

    try {
      const token = localStorage.getItem('token');
      const newStatus = !currentStatus;

      await axios.put(`${config.API_URL}/dataAdmin/toggle-archive/${noTiket}`,
        { is_archived: newStatus },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setShowArchiveModal(false);
      const statusText = newStatus ? 'diarsipkan' : 'diaktifkan kembali';
      setSuccessMessage(`Data dengan tiket ${noTiket} berhasil ${statusText}.`);
      setShowSuccessModal(true);
      await fetchData(showArchivedData);

    } catch (error) {
      console.error('Error toggling archive status:', error);
      setShowArchiveModal(false);
      alert('Gagal mengubah status arsip data.');
    }
  };

  const getViewUrl = (row) => {
    const encodedTicketNo = btoa(row.no_tiket);
    if (row.status === 'Sudah Terintegrasi') {
      return `/rekapdata/${encodedTicketNo}`;
    } else if (row.status === 'Belum Diverifikasi') {
      return userRole === 0
        ? `/admin/verify/${encodedTicketNo}`
        : `/supervisor/approval/${encodedTicketNo}`;
    } else {
      return userRole === 0
        ? `/admin/verify/${encodedTicketNo}`
        : `/supervisor/approval/${encodedTicketNo}`;
    }
  };

  const columns = [
    {
      name: 'No Tiket',
      selector: row => row.no_tiket,
      sortable: !expandedIGT, 
      width: '200px',
      id: 'no_tiket',
      cell: row => {
        if (row.is_igt_summary) {
          return (
            <span
              className="igt-link"
              onClick={() => handleIGTClick(row.igt_code)}
              data-tooltip-id="igt-tooltip"
              data-tooltip-content={`${expandedIGT === row.igt_code ? 'Sembunyikan' : 'Tampilkan'} detail data IGT ${row.igt_code}`}
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
            >
              <span style={{ marginRight: '5px' }}>
                {expandedIGT === row.igt_code ? '▼' : '▶'}
              </span>
              <strong>{row.no_tiket}</strong>
            </span>
          );
        }
        return (
          <span style={row.is_detail_row ? { marginLeft: '20px', fontSize: '0.9em', color: '#666' } : {}}>
            {row.no_tiket}
          </span>
        );
      }
    },
    {
      name: 'Nama IGT',
      selector: row => row.nama_igt,
      sortable: !expandedIGT,
      width: '200px',
      sortFunction: (a, b) => {
        return sortDataWithSecondary([a, b], 'nama_igt', true)[0] === a ? -1 : 1;
      },
      cell: row => {
        const cellStyle = row.is_detail_row ? {
          fontSize: '0.9em',
          color: '#666',
          marginLeft: '20px'
        } : {};

        return (
          <div style={cellStyle}>
            {row.nama_igt.length > 100 ? `${row.nama_igt.substring(0, 100)}...` : row.nama_igt}
          </div>
        );
      }
    },
    {
      name: 'KL',
      selector: row => row.kl,
      sortable: !expandedIGT,
      width: '200px',
      sortFunction: (a, b) => {
        return sortDataWithSecondary([a, b], 'kl', true)[0] === a ? -1 : 1;
      },
      cell: row => {
        const cellStyle = row.is_detail_row ? {
          fontSize: '0.9em',
          color: '#666',
          marginLeft: '20px'
        } : {};

        if (row.is_igt_summary) {
          return <span style={cellStyle}>{row.kl}</span>;
        }

        return (
          <span
            className="kl-link"
            onClick={() => handleKLClick(row.kl)}
            style={cellStyle}
          >
            {row.kl}
          </span>
        );
      }
    },
    {
      name: 'Status',
      selector: row => row.status,
      width: '150px',
      sortable: !expandedIGT,
      sortFunction: (a, b) => {
        return sortDataWithSecondary([a, b], 'status', true)[0] === a ? -1 : 1;
      },
      cell: row => {
        if (row.is_igt_summary) {
          return <span>{row.status}</span>;
        }

        const statusClass = {
          'Sesuai': 'status-sesuai',
          'Tidak Sesuai': 'status-tidak-sesuai',
          'Perlu Perbaikan': 'status-perlu-perbaikan',
          'Sedang Verifikasi': 'status-sedang-verifikasi',
          'Sudah Terintegrasi': 'status-sudah-terintegrasi',
          'Belum Diverifikasi': 'status-belum-diverifikasi',
          'Revisi Pengajuan': 'status-revisi-pengajuan',
          'Disetujui': 'status-disetujui'
        }[row.status] || 'badge';

        const badgeStyle = row.is_detail_row ? {
          fontSize: '0.8em',
          padding: '2px 6px',
          marginLeft: '20px'
        } : {};

        return (
          <span
            className={`badge ${statusClass}`}
            style={badgeStyle}
          >
            {row.status}
          </span>
        );
      }
    },
    {
      name: 'Status Publikasi',
      selector: row => row.jenis_igt === 'Renaksi PKSP' ? (row.status_publikasi || '-') : '-',
      sortable: !expandedIGT,
      width: '150px',
      omit: activeFilter === 'igt-list' && !expandedIGT,
      sortFunction: (a, b) => {
        return sortDataWithSecondary([a, b], 'status_publikasi', true)[0] === a ? -1 : 1;
      },
      cell: row => {
        if (row.is_igt_summary) {
          return <span className="badge status-tidak-berlaku" style={{ backgroundColor: '#6c757d', color: '#fff' }}>-</span>;
        }

        // Status publikasi hanya relevan untuk Renaksi PKSP
        const isRenaksiPKSP = row.jenis_igt === 'Renaksi PKSP';
        if (!isRenaksiPKSP) {
          const badgeStyle = row.is_detail_row ? {
            fontSize: '0.8em',
            padding: '2px 6px',
            marginLeft: '20px',
            backgroundColor: '#6c757d',
            color: '#fff'
          } : { backgroundColor: '#6c757d', color: '#fff' };
          return <span className="badge status-tidak-berlaku" style={badgeStyle}>-</span>;
        }

        const statusPublikasi = row.status_publikasi;
        if (!statusPublikasi) {
          const badgeStyle = row.is_detail_row ? {
            fontSize: '0.8em',
            padding: '2px 6px',
            marginLeft: '20px',
            backgroundColor: '#6c757d',
            color: '#fff'
          } : { backgroundColor: '#6c757d', color: '#fff' };
          return <span className="badge status-tidak-berlaku" style={badgeStyle}>-</span>;
        }

        let badgeClass = 'status-sedang-verifikasi';
        if (statusPublikasi === 'Sudah Publikasi') {
          badgeClass = 'status-sudah-terintegrasi';
        } else if (statusPublikasi === 'Siap Publikasi') {
          badgeClass = 'status-perlu-perbaikan';
        } else {
          badgeClass = 'status-belum-diverifikasi';
        }

        const badgeStyle = row.is_detail_row ? {
          fontSize: '0.8em',
          padding: '2px 6px',
          marginLeft: '20px'
        } : {};

        return (
          <span
            className={`badge ${badgeClass}`}
            style={badgeStyle}
          >
            {statusPublikasi}
          </span>
        );
      }
    },
    {
      name: 'Verifikator',
      selector: row => row.verifikator || '-',
      sortable: !expandedIGT,
      width: '150px',
      omit: activeFilter === 'igt-list' && !expandedIGT,
      sortFunction: (a, b) => {
        return sortDataWithSecondary([a, b], 'verifikator', true)[0] === a ? -1 : 1;
      },
      cell: row => {
        const cellStyle = row.is_detail_row ? {
          fontSize: '0.9em',
          color: '#666',
          marginLeft: '20px'
        } : {};

        return (
          <div
            data-tooltip-id="verifikator-tooltip"
            data-tooltip-content={row.verifikator || '-'}
            style={cellStyle}
          >
            {row.verifikator && row.verifikator.length > 30 ? `${row.verifikator.substring(0, 30)}...` : row.verifikator || '-'}
          </div>
        );
      }
    },
    {
      name: 'Terakhir Diupdate',
      selector: row => row.last_modified ? new Date(row.last_modified) : new Date(0),
      sortable: !expandedIGT,
      width: '150px',
      omit: activeFilter === 'igt-list' && !expandedIGT,
      sortFunction: (a, b) => {
        return sortDataWithSecondary([a, b], 'last_modified', false)[0] === a ? -1 : 1;
      },
      cell: row => {
        const cellStyle = row.is_detail_row ? {
          fontSize: '0.9em',
          color: '	',
          marginLeft: '20px'
        } : {};
        if (row.is_igt_summary) {
          return <div style={cellStyle}>-</div>;
        }

        const displayDate = row.last_modified ? new Date(row.last_modified).toLocaleDateString('id-ID', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }) : '-';

        return <div style={cellStyle}>{displayDate}</div>;
      }
    },
    {
      name: 'Aksi',
      cell: row => {
        if (row.is_igt_summary) {
          return null;
        }

        const isArchived = row.is_archived;

        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Link to={getViewUrl(row)} className="action-button view-button">
              <FaEye /> Lihat
            </Link>

            <div style={row.is_detail_row ? { marginLeft: '0' } : {}}>
              <button
                className={`btn btn-sm ${isArchived ? 'btn-success' : 'btn-confirm'}`}
                onClick={() => handleToggleArchive(row.no_tiket, isArchived)}
              >
                {isArchived ? <FaFolderOpen /> : <FaArchive />}
                {isArchived ? 'Aktifkan' : 'Arsipkan'}
              </button>
            </div>
          </div>
        );
      },
      width: '240px',
      omit: activeFilter === 'igt-list' && !expandedIGT,
    }
  ];

  const conditionalRowStyles = [
    {
      when: row => row.is_igt_summary,
      style: {
        backgroundColor: '#f8f9fa',
        fontWeight: 'bold',
        borderLeft: '4px solid #007bff',
      },
    },
    {
      when: row => row.is_detail_row,
      style: {
        backgroundColor: '#fafbfc',
        borderLeft: '2px solid #e9ecef',
        paddingLeft: '20px',
      },
    },
    {
      when: row => row.is_archived,
      style: {
        backgroundColor: '#fff3cd',
        borderLeft: '3px solid #ffc107',
      },
    },
    {
      when: row => {
        return (row.is_published === false || !row.is_published) && !row.is_igt_summary && !row.is_detail_row;
      },
      style: {
        backgroundColor: '#fef3c7',
        borderLeft: '4px solid #fbbf24',
      },
    },
  ];

  const menuItems = userRole === 2 ? getSupervisorMenuItems() : getAdminMenuItems();
  const roleLabel = userRole === 2 ? "Supervisor" : "Admin";
  const profilePath = userRole === 2 ? "/supervisor/profile" : "/admin/profile";

  return (
    <DashboardLayout menuItems={menuItems} userRole={roleLabel}>
      {/* Email Reminder Banner */}
      <EmailReminderBanner userEmail={userEmail} profilePath={profilePath} />
      
      <div className="container mt-4">
        {/* Welcome Card */}
        <div className="card welcome-card mb-4">
        <div className="card-body">
          <h2 className="welcome-title">Selamat Datang, {username}!</h2>
          <p className="welcome-text">Dashboard Monitoring Integrasi Data IGT</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4">
        {/* KL Sudah Upload */}
        <div className="col-md">
          <div
            className={`card stat-card stat-kl ${activeFilter === 'kl-list' ? 'active' : ''}`}
            onClick={() => handleStatCardClick('kl-list')}
          >
            <div className="card-body">
              <h5 className="stat-title">KL Sudah Upload</h5>
              <h2 className="stat-value">{stats.uploadedKL}</h2>
              <p className="stat-desc">Total K/L yang pernah upload</p>
              <div className="card-overlay">
                <span>Lihat Daftar K/L</span>
              </div>
            </div>
          </div>
        </div>

        {/* Total IGT */}
        <div className="col-md">
          <div
            className={`card stat-card stat-igt ${activeFilter === 'igt-list' ? 'active' : ''}`}
            onClick={() => handleStatCardClick('igt-list')}
          >
            <div className="card-body">
              <h5 className="stat-title">Total IGT</h5>
              <h2 className="stat-value">{stats.totalUniqueIGT}</h2>
              <p className="stat-desc">Jumlah Data IGT</p>
              <div className="card-overlay">
                <span>Lihat Daftar IGT</span>
              </div>
            </div>
          </div>
        </div>

        {/* IGT Sudah Terintegrasi */}
        <div className="col-md">
          <div
            className={`card stat-card stat-integrated ${activeFilter === 'integrated' ? 'active' : ''}`}
            onClick={() => handleStatCardClick('integrated')}
          >
            <div className="card-body">
              <h5 className="stat-title">IGT Sudah Terintegrasi</h5>
              <h2 className="stat-value">{stats.integratedData}</h2>
              <p className="stat-desc">Jumlah IGT yang Sudah Terintegrasi</p>
              <div className="card-overlay">
                <span>Lihat Data Terintegrasi</span>
              </div>
            </div>
          </div>
        </div>

        {/* IGT Belum Terintegrasi */}
        <div className="col-md">
          <div
            className={`card stat-card stat-non-integrated ${activeFilter === 'non-integrated' ? 'active' : ''}`}
            onClick={() => handleStatCardClick('non-integrated')}
          >
            <div className="card-body">
              <h5 className="stat-title">IGT Belum Terintegrasi</h5>
              <h2 className="stat-value">{stats.nonIntegratedData}</h2>
              <p className="stat-desc">Jumlah IGT yang belum terintegrasi</p>
              <div className="card-overlay">
                <span>Lihat Data Belum Terintegrasi</span>
              </div>
            </div>
          </div>
        </div>

        {/* Total Data */}
        <div className="col-md">
          <div
            className={`card stat-card stat-total ${activeFilter === null && !showArchivedData ? 'active' : ''}`}
            onClick={() => handleStatCardClick('all')}
          >
            <div className="card-body">
              <h5 className="stat-title">Total Data</h5>
              <h2 className="stat-value">{stats.totalData}</h2>
              <p className="stat-desc">Jumlah seluruh data yang sudah diupload</p>
              <div className="card-overlay">
                <span>Lihat Semua Data</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Chart */}
      <div className="card shadow-lg p-4 mb-4">
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <div className="search-container">
            <input
              type="text"
              className="filter-input"
              placeholder="Cari No Tiket, Nama, KL, atau Status"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button className="btn btn-cari search-button"
              onClick={handleSearch}
              disabled={searchLoading}
            >
              <i className="fas fa-search"></i> Cari
            </button>

            {/* Toggle button untuk arsip/aktif */}
            <div className="archive-toggle">
              <button
                className={`btn ${showArchivedData ? 'btn-aktif' : 'btn-arsip'}`}
                onClick={() => handleStatCardClick('archived')}
              >
                <i className={`fas ${showArchivedData ? 'fa-folder-open' : 'fa-archive'}`}></i>
                {showArchivedData ? ' Data Aktif' : ' Data Arsip'}
              </button>
            </div>
          </div>
        </div>

        {/* Filter aktif */}
        {activeFilter && (
          <div className="active-filter-container mt-3">
            <div className="active-filter-badge">
              <span>Filter aktif: <strong>{filterTitle}</strong></span>
              <button
                className="btn btn-sm btn-light ms-2"
                onClick={() => {
                  setActiveFilter(null);
                  setExpandedIGT(null);
                  setFilterTitle(showArchivedData ? 'Semua Data Arsip' : 'Semua Data Aktif');
                  setFilteredData(sortDataByTicket(dataList, false));
                }}
              >
                &times; Tampilkan Semua
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Chart */}
      {!showArchivedData && (
        <div className="card shadow-lg p-4 mb-4">
          <DataChart
            data={chartData}
            onPieClick={handleChartClick}
            activeFilter={activeFilter}
          />
        </div>
      )}

      {/* Data Table */}
      <div className="card shadow-lg p-4 printable" ref={tableRef}>
        <h3 className="title">
          {filterTitle}
          <span className="badge bg-primary ms-2">{filteredData.length} Data</span>
        </h3>

        {error && <div className="alert-danger">{error}</div>}
        <DataTable
          columns={columns}
          data={filteredData}
          progressPending={loading}
          progressComponent={<LoadingSpinner message="Memuat data..." type="spinner" size="medium" />}
          pagination
          highlightOnHover
          pointerOnHover
          defaultSortFieldId="no_tiket"
          defaultSortAsc={false}
          sortServer={false}
          noDataComponent={<p className="text-center text-muted">Tidak ada data yang tersedia.</p>}
          responsive
          conditionalRowStyles={conditionalRowStyles}
        />
      </div>
      <Tooltip id="ticket-tooltip" />
      <Tooltip id="nama-igt-tooltip" />
      <Tooltip id="kl-tooltip" />
      <Tooltip id="verifikator-tooltip" />
      <Tooltip id="igt-tooltip" />
      <Tooltip id="archive-tooltip" />
      {searchLoading && (
        <div className="loading-overlay-fullscreen">
          <LoadingSpinner message="Mencari data..." type="dots" size="large" />
        </div>
      )}
      <Modal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        type="danger"
        title={archiveTicket.currentStatus ? 'Konfirmasi Aktivasi' : 'Konfirmasi Arsip'}
        message={`Apakah Anda yakin ingin ${archiveTicket.currentStatus ? 'mengaktifkan kembali' : 'mengarsipkan'} data dengan tiket No ${archiveTicket.noTiket}?`}
        onConfirm={handleConfirmArchive}
        confirmText={archiveTicket.currentStatus ? 'Ya, Aktifkan' : 'Ya, Arsipkan'}
      />

      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        type="success"
        title="Berhasil"
        message={successMessage}
      />
      </div>
    </DashboardLayout>
  );
};

export default AdminPage;